import FingerprintJS from '@fingerprintjs/fingerprintjs';
import CryptoJS from 'crypto-js';

/**
 * Device fingerprint data collected from browser/device.
 * Used for session binding and device limit enforcement.
 */
export interface DeviceFingerprintData {
  userAgent: string;
  screenResolution: string;
  timeZone: string;
  webGLFingerprint: string | null;
  audioFingerprint: string | null;
}

/**
 * Device fingerprinting service using @fingerprintjs/fingerprintjs.
 * Collects browser/device characteristics for DRM session binding.
 */
class DeviceFingerprintingService {
  private fpPromise: Promise<any> | null = null;
  private cachedFingerprintHash: string | null = null;

  /**
   * Initialize FingerprintJS agent (call once on app load)
   */
  async initialize(): Promise<void> {
    if (!this.fpPromise) {
      this.fpPromise = FingerprintJS.load();
    }
    await this.fpPromise;
  }

  /**
   * Collect device fingerprint data from browser.
   * Must call initialize() first.
   */
  async collectFingerprint(): Promise<DeviceFingerprintData> {
    if (!this.fpPromise) {
      throw new Error('DeviceFingerprintingService not initialized. Call initialize() first.');
    }

    const fp = await this.fpPromise;
    const result = await fp.get();

    // Extract components
    const components = result.components;

    return {
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      webGLFingerprint: this.getWebGLFingerprint(components),
      audioFingerprint: this.getAudioFingerprint(components),
    };
  }

  /**
   * Compute SHA-256 hash of device fingerprint data.
   * Backend expects this hash for session binding.
   */
  computeHash(fingerprintData: DeviceFingerprintData): string {
    const components = [
      fingerprintData.userAgent,
      fingerprintData.screenResolution,
      fingerprintData.timeZone,
      fingerprintData.webGLFingerprint || '',
      fingerprintData.audioFingerprint || '',
    ].join('|');

    const hash = CryptoJS.SHA256(components);
    return CryptoJS.enc.Base64.stringify(hash);
  }

  /**
   * Convenience method: collect fingerprint and compute hash in one call.
   * Caches the hash after first computation to ensure consistency across API calls.
   */
  async getFingerprintHash(): Promise<string> {
    // Return cached hash if available (ensures same hash for entire session)
    if (this.cachedFingerprintHash) {
      return this.cachedFingerprintHash;
    }

    // Compute and cache hash for first time
    const fingerprintData = await this.collectFingerprint();
    const hash = this.computeHash(fingerprintData);
    this.cachedFingerprintHash = hash;

    console.log('Device fingerprint hash computed and cached');
    return hash;
  }

  /**
   * Clear cached fingerprint hash.
   * Call this when user logs out or session terminates.
   */
  clearCache(): void {
    this.cachedFingerprintHash = null;
    console.log('Device fingerprint hash cache cleared');
  }

  /**
   * Get complete fingerprint data with hash (for API requests).
   */
  async getFingerprintWithHash(): Promise<DeviceFingerprintData & { hash: string }> {
    const fingerprintData = await this.collectFingerprint();
    const hash = this.computeHash(fingerprintData);

    return {
      ...fingerprintData,
      hash,
    };
  }

  // Private helper methods

  private getWebGLFingerprint(components: any): string | null {
    try {
      const webgl = components.webgl?.value;
      if (webgl) {
        return JSON.stringify(webgl);
      }

      // Fallback: manual WebGL fingerprinting
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

      if (!gl) return null;

      // @ts-ignore - WebGL context typings
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (!debugInfo) return null;

      // @ts-ignore
      const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
      // @ts-ignore
      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);

      return `${vendor}|${renderer}`;
    } catch (error) {
      console.warn('WebGL fingerprinting failed:', error);
      return null;
    }
  }

  private getAudioFingerprint(components: any): string | null {
    try {
      const audio = components.audio?.value;
      if (audio) {
        return JSON.stringify(audio);
      }

      // Fallback: manual audio fingerprinting
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContext) return null;

      const context = new AudioContext();
      const oscillator = context.createOscillator();
      const analyser = context.createAnalyser();
      const gainNode = context.createGain();
      const scriptProcessor = context.createScriptProcessor(4096, 1, 1);

      gainNode.gain.value = 0; // Mute
      oscillator.connect(analyser);
      analyser.connect(scriptProcessor);
      scriptProcessor.connect(gainNode);
      gainNode.connect(context.destination);

      oscillator.start(0);

      // Simple audio fingerprint based on context properties
      const fingerprint = [
        context.sampleRate,
        context.destination.maxChannelCount,
        context.destination.numberOfInputs,
        context.destination.numberOfOutputs,
      ].join('|');

      oscillator.stop();
      context.close();

      return fingerprint;
    } catch (error) {
      console.warn('Audio fingerprinting failed:', error);
      return null;
    }
  }
}

// Export singleton instance
export const deviceFingerprintingService = new DeviceFingerprintingService();
